import {
  AutoSizer,
  InfiniteLoader,
  Grid,
  WindowScroller,
  CellMeasurer,
  CellMeasurerCache,
} from 'react-virtualized';
import { useLazyQuery } from '@apollo/client';
import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from '@/i18n/routing';

import { SearchListingsQuery } from '@/query';
import { ListingsCard } from './Card';
import { SkeletonCard } from './Skeleton';

import styles from './List.module.css';
import { useLocale } from 'next-intl';
import { usePathname } from 'next/navigation';
export const DEFAULT_WIDTH = 640;
const DEFAULT_HEIGHT = 436;

const defaultSizePoints = {
  640: DEFAULT_HEIGHT,
  953: 474,
  1420: 474,
  1540: 474,
};

const breakPointsWidth = {
  650: 1,
  960: 2,
  1420: 2,
  1540: 3,
  1560: 2,
};

export const ListComponent = ({
  children,
  query,
  width,
  initialData,
  nextPage,
  limit = 20,
  disableScrollLoad,
  variables,
  searchState,
  isSearchStateIniting,
  pageName,
}) => {
  const locale = useLocale();
  const pathname = usePathname();
  const masonryRef = useRef(null);
  const [isHydrated, setIsHydrated] = useState(false);
  const [listingItems, setListingItems] = useState(
    initialData?.collection || []
  );
  const [totalCount, setTotalCount] = useState(
    disableScrollLoad
      ? Math.min(listingItems?.length || 0, limit)
      : initialData?.metadata?.totalCount || 0
  );
  const [totalPages, setTotalPages] = useState(
    disableScrollLoad ? 1 : initialData?.metadata?.totalPages || 1
  );
  const [isLoaded, setIsLoaded] = useState(false);
  const [offset, setOffset] = useState(1);

  const [fetchNextPage, { loading, called, data, error }] =
    useLazyQuery(SearchListingsQuery);

  const fetchNext = (params, offset, ctrl) => {
    if (disableScrollLoad) {
      return;
    }

    fetchNextPage({
      variables: {
        ...params,
        locale,
        limitValue: 20,
        page: offset || 1,
      },
      context: {
        fetchOptions: ctrl
          ? {
              signal: ctrl.signal,
            }
          : undefined,
      },
    }).then((response) => {
      if (response?.data?.availableListings) {
        setTotalPages(response?.data.availableListings.metadata.totalPages);
        setTotalCount(response?.data.availableListings.metadata.totalCount);
        setListingItems((prev) =>
          offset === 1 || !offset
            ? response?.data.availableListings.collection
            : [...prev, ...response?.data.availableListings.collection]
        );
      }
    });
  };

  const loadMoreRows = () => {
    if (offset < totalPages || offset * limit < totalCount) {
      const newOffset = offset + 1;
      fetchNext(variables, newOffset);
      setOffset(newOffset);
    }
  };

  useEffect(() => {
    if (isSearchStateIniting) return;
    const ctrl = new AbortController();
    fetchNext(variables, offset);
    return () => ctrl.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isHydrated) {
      return setIsHydrated(true);
    }
  }, [offset, variables, isHydrated]);

  const isEmpty =
    isHydrated && !loading && !listingItems?.length && !totalCount;
  const isAbortError = error ? String(error).includes('aborted') : false;
  const reqError = Boolean(error && !isAbortError);
  const isError = called && !loading && reqError;
  const isPlaceholderNeed =
    isHydrated &&
    !isSearchStateIniting &&
    !loading &&
    !isError &&
    (!totalCount || totalCount < 3);
  const isInexactNeed =
    isHydrated &&
    !isSearchStateIniting &&
    !loading &&
    !isError &&
    Number(searchState.inexactStay) < 2;
  const isListVisible = !!listingItems.length;
  const isLoading = !called || loading || !data;
  const isScreenLoading =
    !isHydrated ||
    isSearchStateIniting ||
    ((!called || !(listingItems || initialData || data || reqError)) &&
      !listingItems.length);

  useEffect(() => {
    if (isLoading) return;

    if (totalPages === offset) setIsLoaded(true);
  }, [isLoading, isLoaded, data]);

  const handleResetStates = useCallback(() => {
    setListingItems([]);
    setIsLoaded(false);
    setTotalCount(0);
    setOffset(1);
  }, []);

  const cardsColumn = Object.keys(breakPointsWidth).reduceRight((acc, curr) => {
    if (curr > width) acc = breakPointsWidth[curr];
    return acc;
  }, 2);
  const columnHeight = Object.keys(defaultSizePoints).reduceRight(
    (acc, curr) => {
      if (curr > width) acc = defaultSizePoints[curr];
      return acc;
    },
    2
  );
  const rowCount = Math.ceil(totalCount / cardsColumn) || 0;
  const columnWidth = Math.round(width / cardsColumn);

  const cache = new CellMeasurerCache({
    defaultHeight: columnHeight,
    defaultWidth: columnWidth,
    fixedWidth: true,
  });

  const itemRenderer = ({
    key,
    columnIndex,
    rowIndex,
    index,
    parent,
    style,
  }) => {
    return listingItems.length > index ? (
      <CellMeasurer
        cache={cache}
        key={key}
        parent={parent}
        rowIndex={rowIndex}
        columnIndex={columnIndex}
      >
        <div style={style}>
          {listingItems[index] ? (
            <ListingsCard
              list={listingItems[index]}
              location={searchState.location}
              guests={searchState.guests}
              index={index}
              date={searchState.date}
              tab={searchState.tab}
              rooms={searchState.rooms}
              rating={searchState.rating}
              price={searchState.price}
              amenities={searchState.amenities}
              key={key}
              dataTestId={key}
              pageName={pageName}
            />
          ) : (
            <SkeletonCard />
          )}
        </div>
      </CellMeasurer>
    ) : null;
  };

  const list = isEmpty ? null : (
    <AutoSizer
      className="!w-full [&>div]:!w-full"
      disableHeight={true}
      defaultWidth={DEFAULT_WIDTH}
    >
      {({ width }) => {
        return (
          <WindowScroller>
            {({
              height,
              isScrolling,
              onChildScroll,
              scrollTop,
              registerChild,
            }) => (
              <div
                className={isHydrated ? '!w-full' : '!w-full opacity-0'}
                ref={(el) => registerChild(el)}
              >
                <InfiniteLoader
                  rowCount={rowCount || 1000}
                  loadMoreRows={loadMoreRows}
                  isRowLoaded={({ index }) =>
                    !!listingItems[index * cardsColumn + cardsColumn]
                  }
                >
                  {({ onRowsRendered, registerChild }) => (
                    <Grid
                      ref={(node) => {
                        masonryRef.current = node;
                        registerChild(node);
                      }}
                      cellRenderer={({
                        columnIndex,
                        key,
                        rowIndex,
                        parent,
                        style,
                      }) => {
                        const index = rowIndex * cardsColumn + columnIndex;

                        return itemRenderer({
                          key,
                          style:
                            isHydrated && style.top
                              ? style
                              : { ...style, top: DEFAULT_HEIGHT * rowIndex },
                          parent,
                          columnIndex,
                          rowIndex,
                          index,
                        });
                      }}
                      className={`${styles.list}`}
                      columnCount={cardsColumn}
                      columnWidth={Math.round(width / cardsColumn)}
                      rowCount={rowCount}
                      onSectionRendered={({
                        columnStartIndex,
                        columnStopIndex,
                        rowStartIndex,
                        rowStopIndex,
                      }) => {
                        const startIndex =
                          rowStartIndex * cardsColumn + columnStartIndex;
                        const stopIndex =
                          rowStopIndex * cardsColumn + columnStopIndex;

                        onRowsRendered({
                          startIndex,
                          stopIndex,
                        });
                      }}
                      rowHeight={isHydrated ? cache.rowHeight : DEFAULT_HEIGHT}
                      deferredMeasurementCache={cache}
                      onScroll={onChildScroll}
                      isScrolling={isScrolling}
                      scrollTop={scrollTop}
                      height={isHydrated ? height : DEFAULT_HEIGHT * rowCount}
                      width={width}
                      autoHeight
                    />
                  )}
                </InfiniteLoader>
                {!isHydrated && nextPage ? (
                  <Link
                    href={{ pathname, query: { ...query, page: nextPage } }}
                  >
                    {nextPage}
                  </Link>
                ) : null}
              </div>
            )}
          </WindowScroller>
        );
      }}
    </AutoSizer>
  );

  return children({
    list,
    location: searchState.location,
    totalCount,
    isScreenLoading,
    isListVisible,
    isEmpty,
    isError,
    isPlaceholderNeed,
    isInexactNeed,
    handleResetStates,
  });
};
